import React, { useEffect, useState } from "react";
import { siteData } from "../data/mock";
import { ArrowRight, Calendar, Clock, Sparkles, GraduationCap, BookOpen, Briefcase, Building2, Radio } from "lucide-react";

const iconMap = { GraduationCap, BookOpen, Briefcase, Building2, Radio };

const calcRemaining = (targetIso) => {
  const target = new Date(targetIso).getTime();
  const now = new Date().getTime();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hrs = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const min = Math.floor((diff / (1000 * 60)) % 60);
  const sec = Math.floor((diff / 1000) % 60);
  return { days, hrs, min, sec };
};

const pad = (n) => String(n).padStart(2, "0");

const TimeCell = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[48px] sm:min-w-[60px]">
    <span className="number-tick text-[38px] sm:text-[48px] md:text-[54px] leading-none font-medium">
      {pad(value)}
    </span>
    <span className="mt-2 text-[9px] sm:text-[10px] tracking-[0.24em] sm:tracking-[0.28em] uppercase text-slate-400">
      {label}
    </span>
  </div>
);

const Hero = () => {
  const { hero, webinar, credentials } = siteData;
  const [t, setT] = useState(calcRemaining(webinar.target));

  useEffect(() => {
    const id = setInterval(() => setT(calcRemaining(webinar.target)), 1000);
    return () => clearInterval(id);
  }, [webinar.target]);

  return (
    <section className="hero-radial pt-[70px] sm:pt-[78px] relative">
      {/* subtle grid overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(120,200,220,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(120,200,220,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)",
        }}
      />

      <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-14 lg:pt-16 pb-16 sm:pb-20 lg:pb-24 relative">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-12 items-start">
          <div>
            <div className="inline-flex items-center gap-2 chip-blue px-4 py-2 rounded-full">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5EC7F5] opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#5EC7F5]" />
              </span>
              <span className="text-[11px] tracking-[0.32em] uppercase">
                {hero.eyebrow}
              </span>
            </div>

            <h1 className="font-display text-[44px] sm:text-[56px] md:text-[72px] lg:text-[86px] leading-[0.98] mt-8 tracking-tight">
              <span className="text-gradient-title">{hero.titlePre}</span>{" "}
              <span className="text-gradient-blue">{hero.titleHighlight}</span>
              <br />
              <span className="text-gradient-title">{hero.titlePost}</span>
            </h1>
            <p className="text-slate-300 text-[18px] sm:text-[22px] md:text-[26px] mt-5 font-light">
              {hero.subtitle}
            </p>

            <div className="mt-6 h-px divider-line max-w-[520px]" />

            <p className="text-slate-300/85 text-[15px] leading-relaxed mt-6 max-w-[560px]">
              Join global professor and Chief Data Scientist{" "}
              <span className="text-white font-medium">Kent Oliver Bhupathi</span>{" "}
              (formerly NYU & Columbia University) for a live webinar on the AI skills, roles and opportunities shaping the next decade of work.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#register"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#5EC7F5] text-[#062026] font-semibold text-sm glow-blue-btn hover:bg-[#8ad8f7] transition-colors"
              >
                {hero.cta} <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#learn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-slate-300 border hairline hover:border-[#5EC7F5]/40 hover:text-white transition-colors text-sm"
              >
                See What We'll Cover
              </a>
            </div>
          </div>

          {/* Right card */}
          <div className="card-glow rounded-2xl p-5 sm:p-7 lg:p-8 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] tracking-[0.28em] sm:tracking-[0.32em] uppercase text-[#5EC7F5]">
                <Sparkles className="w-3.5 h-3.5" />
                {webinar.label}
              </div>
              <span className="chip-blue text-[10px] tracking-[0.24em] uppercase px-3 py-1 rounded-full">
                {webinar.tag}
              </span>
            </div>

            <h3 className="font-display text-[26px] sm:text-[30px] md:text-[32px] leading-[1.05] mt-5 sm:mt-6 text-white">
              {webinar.title}
            </h3>
            <p className="text-slate-400 text-sm mt-3">{webinar.subtitle}</p>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6 sm:mt-7">
              <div className="rounded-xl border hairline p-3 sm:p-4 bg-[#0a1626]/60">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-slate-400">
                  <Calendar className="w-3.5 h-3.5 text-[#5EC7F5]" /> Date
                </div>
                <div className="text-white font-display text-[20px] sm:text-[24px] leading-none mt-3">{webinar.dateLabel}</div>
                <div className="text-slate-400 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase mt-2">{webinar.dateSub}</div>
              </div>
              <div className="rounded-xl border hairline p-3 sm:p-4 bg-[#0a1626]/60">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-slate-400">
                  <Clock className="w-3.5 h-3.5 text-[#5EC7F5]" /> Time
                </div>
                <div className="text-white font-display text-[20px] sm:text-[24px] leading-none mt-3">{webinar.timeLabel}</div>
                <div className="text-slate-400 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase mt-2">{webinar.timeSub}</div>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 rounded-xl border hairline p-4 sm:p-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-slate-400 text-center">
                Starts In
              </div>
              <div className="flex items-center justify-between mt-4">
                <TimeCell value={t.days} label="Days" />
                <span className="text-slate-600 text-xl sm:text-2xl -mt-4">·</span>
                <TimeCell value={t.hrs} label="Hrs" />
                <span className="text-slate-600 text-xl sm:text-2xl -mt-4">·</span>
                <TimeCell value={t.min} label="Min" />
                <span className="text-slate-600 text-xl sm:text-2xl -mt-4">·</span>
                <TimeCell value={t.sec} label="Sec" />
              </div>
            </div>

            <a
              href="#register"
              className="mt-6 flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-white text-[#062026] font-semibold text-sm hover:bg-slate-100 transition-colors"
            >
              {webinar.cta} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Credentials strip */}
      <div className="border-t border-b hairline">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-4 sm:py-5 flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-10 gap-y-3">
          {credentials.map((c, i) => {
            const Icon = iconMap[c.icon] || Radio;
            return (
              <div
                key={c.label}
                className="flex items-center gap-2 text-[11px] sm:text-[12px] tracking-[0.14em] text-slate-300"
              >
                <Icon className="w-3.5 h-3.5 text-[#5EC7F5]" />
                <span>{c.label}</span>
                {i < credentials.length - 1 && (
                  <span className="text-slate-600 ml-4 sm:ml-6 hidden md:inline">·</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
